import React, { useState, useEffect } from 'react';
import { X, Check, AlertCircle, FileText, Shield, Database } from 'lucide-react';
import { Button } from '../ui/button';
import api from '../../lib/api';

const CONSENT_TYPES = [
  {
    type: 'terms_of_service',
    title: 'Terms of Service',
    icon: FileText,
    description: 'Acceptance of platform terms and conditions',
    required: true
  },
  {
    type: 'privacy_policy',
    title: 'Privacy Policy',
    icon: Shield,
    description: 'How we collect, use, and protect your data',
    required: true
  },
  {
    type: 'fundability_disclaimer',
    title: 'Fundability Disclaimer',
    icon: AlertCircle,
    description: 'Fundability scores are estimates for informational purposes only',
    required: true
  },
  {
    type: 'data_processing',
    title: 'Data Processing Agreement',
    icon: Database,
    description: 'Permission to process your business data',
    required: false
  }
];

export default function ConsentModal({ isOpen, onClose, onConsentsAccepted }) {
  const [acceptedConsents, setAcceptedConsents] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedConsent, setExpandedConsent] = useState(null);

  // Check which consents user has already accepted
  useEffect(() => {
    if (isOpen) {
      checkExistingConsents();
    }
  }, [isOpen]);

  const checkExistingConsents = async () => {
    try {
      const response = await api.get('/consent/status');
      const accepted = new Set();
      Object.keys(response.data.consents).forEach(type => {
        accepted.add(type);
      });
      setAcceptedConsents(accepted);
    } catch (err) {
      console.error('Failed to check consents:', err);
    }
  };

  const toggleConsent = (type) => {
    setAcceptedConsents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  };

  const handleAcceptAll = async () => {
    setLoading(true);
    setError('');

    try {
      // Accept all required consents
      const requiredConsents = CONSENT_TYPES.filter(c => c.required).map(c => c.type);
      
      for (const consentType of requiredConsents) {
        if (!acceptedConsents.has(consentType)) {
          await api.post('/consent/accept', {
            consent_type: consentType,
            version: '1.0'
          });
        }
      }

      // Call success callback
      if (onConsentsAccepted) {
        onConsentsAccepted();
      }

      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to accept consents');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomAccept = async () => {
    setLoading(true);
    setError('');

    try {
      // Check if all required consents are accepted
      const requiredConsents = CONSENT_TYPES.filter(c => c.required).map(c => c.type);
      const missingRequired = requiredConsents.filter(type => !acceptedConsents.has(type));

      if (missingRequired.length > 0) {
        setError('Please accept all required consents to continue');
        setLoading(false);
        return;
      }

      // Accept all selected consents
      for (const consentType of Array.from(acceptedConsents)) {
        await api.post('/consent/accept', {
          consent_type: consentType,
          version: '1.0'
        });
      }

      // Call success callback
      if (onConsentsAccepted) {
        onConsentsAccepted();
      }

      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to accept consents');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const requiredConsents = CONSENT_TYPES.filter(c => c.required);
  const allRequiredAccepted = requiredConsents.every(c => acceptedConsents.has(c.type));

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">User Consent Required</h2>
              <p className="text-slate-400 text-sm">
                Please review and accept the following to continue using CorpCraft
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors"
              disabled={!allRequiredAccepted}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Consent List */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-red-400 text-sm">
              {error}
            </div>
          )}

          {CONSENT_TYPES.map((consent) => {
            const Icon = consent.icon;
            const isAccepted = acceptedConsents.has(consent.type);
            const isExpanded = expandedConsent === consent.type;

            return (
              <div
                key={consent.type}
                className={`border rounded-lg p-4 transition-all ${
                  isAccepted
                    ? 'border-green-500/50 bg-green-500/5'
                    : consent.required
                    ? 'border-blue-500/50 bg-blue-500/5'
                    : 'border-slate-700 bg-slate-800/50'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-colors ${
                      isAccepted
                        ? 'border-green-500 bg-green-500'
                        : 'border-slate-600 hover:border-slate-400'
                    }`}
                    onClick={() => toggleConsent(consent.type)}
                  >
                    {isAccepted && <Check className="w-3 h-3 text-white" />}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="w-5 h-5 text-blue-400" />
                      <h3 className="font-semibold">{consent.title}</h3>
                      {consent.required && (
                        <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">
                          Required
                        </span>
                      )}
                    </div>
                    <p className="text-slate-400 text-sm mb-2">{consent.description}</p>

                    <button
                      onClick={() => setExpandedConsent(isExpanded ? null : consent.type)}
                      className="text-blue-400 text-sm hover:underline"
                    >
                      {isExpanded ? 'Hide details' : 'View details'}
                    </button>

                    {isExpanded && (
                      <div className="mt-3 p-3 bg-slate-800 rounded border border-slate-700 text-sm text-slate-300">
                        <p className="mb-2">
                          <strong>What you're agreeing to:</strong>
                        </p>
                        {consent.type === 'terms_of_service' && (
                          <ul className="list-disc list-inside space-y-1 text-slate-400">
                            <li>Platform usage rules and guidelines</li>
                            <li>User responsibilities and obligations</li>
                            <li>Service limitations and disclaimers</li>
                          </ul>
                        )}
                        {consent.type === 'privacy_policy' && (
                          <ul className="list-disc list-inside space-y-1 text-slate-400">
                            <li>Data collection and usage practices</li>
                            <li>Third-party data sharing policies</li>
                            <li>Your privacy rights and choices</li>
                          </ul>
                        )}
                        {consent.type === 'fundability_disclaimer' && (
                          <ul className="list-disc list-inside space-y-1 text-slate-400">
                            <li>Fundability scores are estimates only</li>
                            <li>Not financial, legal, or tax advice</li>
                            <li>Consult professionals before decisions</li>
                          </ul>
                        )}
                        {consent.type === 'data_processing' && (
                          <ul className="list-disc list-inside space-y-1 text-slate-400">
                            <li>Processing of business structure data</li>
                            <li>Credit intelligence calculations</li>
                            <li>IP registry and valuation analysis</li>
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-800 bg-slate-900/50">
          <div className="flex gap-3">
            <Button
              onClick={handleAcceptAll}
              disabled={loading}
              className="flex-1 gradient-accent hover:opacity-90"
            >
              {loading ? 'Processing...' : 'Accept All Required'}
            </Button>
            <Button
              onClick={handleCustomAccept}
              disabled={loading || !allRequiredAccepted}
              variant="outline"
              className="flex-1 border-slate-700"
            >
              {loading ? 'Processing...' : 'Accept Selected'}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-3 text-center">
            By accepting, you agree to our platform policies. Version 1.0
          </p>
        </div>
      </div>
    </div>
  );
}
